// How a finished play is named and counted when it is shared
// (docs/01-Product/Sharing.md): the caption under the share image, which the
// Worker's share page repeats. Counts, a score and a time; never a word.
import type { Layer } from '../content/plan';
import type { Mode, Variant } from '../level/schema';
import { playName } from '../level/names';

const two = (n: number) => String(n).padStart(2, '0');

/** m:ss, or h:mm:ss from an hour. */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${two(m)}:${two(s)}` : `${m}:${two(s)}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 15" from a YYYY-MM-DD date, in English. */
export function formatShortDate(date: string): string {
  const [, m, d] = date.split('-').map(Number);
  return `${MONTHS[(m ?? 1) - 1]} ${d ?? 1}`;
}

/** "Oct 15, 2026" from a YYYY-MM-DD date, in English. */
export function formatFullDate(date: string): string {
  const [y] = date.split('-');
  return `${formatShortDate(date)}, ${y}`;
}

/** What the caption counts: a subset of the game's play stats, which the Worker can work out too. */
export interface CaptionStats {
  mode: Mode;
  variant: Variant;
  /** The night's own level, or the week's (named XL); the night's when absent. */
  layer?: Layer;
  /** The night, or the week's Monday for a week's play. */
  date: string;
  /** Letter stars in the player's constellations. */
  starsUsed: number;
  constellations: number;
  /** Apogee: the final score; null otherwise. */
  score: number | null;
  /** Start to finish; null while the play is open. */
  elapsedMs: number | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The image's caption, in the order Aaron listed: date, stars,
 * constellations, the score in Apogee, the completion time. The play's name
 * leads.
 */
export function captionParts(stats: CaptionStats): string[] {
  const parts = [playName(stats.mode, stats.variant, stats.layer), formatFullDate(stats.date), plural(stats.starsUsed, 'star', 'stars'), plural(stats.constellations, 'constellation', 'constellations')];
  if (stats.mode === 'apogee' && stats.score !== null) parts.push(plural(stats.score, 'point', 'points'));
  if (stats.elapsedMs !== null) parts.push(formatDuration(stats.elapsedMs));
  return parts;
}
